//Node Modules
import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState, useRecoilValue } from "recoil";
import _ from "lodash";
import { v4 as uuid } from "uuid";
import { useNavigate, useSearchParams } from "react-router-dom";

//GraphQL

//BinaryForge Components
import FeatureItem from "./FeatureItem";
import { PillItem } from "../helpers";
import { ConnectivityPill } from "../device/DeviceStatusPills";

//3rd Party Components
import { FilterMatchMode, FilterOperator } from "primereact/api";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { MultiSelect } from "primereact/multiselect";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { Tooltip } from "primereact/tooltip";

//Atoms
import { devicesListAtom, selectedDeviceAtom } from "../../atoms/devices";
import { deviceSensorAlarmListAtom } from "../../atoms/alarm";

//Helpers
import { exportDate } from "../../helpers/DateUtils";
import { useDeviceOfflineState } from "../../helpers/useDeviceOfflineState";
import { RSSI_BANDS, getRssiBand } from "../../helpers/Device";
import { filterApplyTemplate } from "../../config/datatable";
import { inputDeviceOptions, outputDeviceOptions, useSensorActuatorLabels } from "../../config/device";

//Other

const parseDeviceState = (device) => {
	try {
		return JSON.parse(device.reported_state) ?? {};
	} catch {
		return {};
	}
};

const getDeviceRssi = (device) => Number(parseDeviceState(device)?.wifi?.rssi);
const getDeviceBattery = (device) => Number(parseDeviceState(device)?.battery?.percentage);
const hasClosedValve = (device) => {
	const state = parseDeviceState(device);
	return [state?.valves?.valve1?.status, state?.valves?.valve2?.status, state?.relay?.status].some(
		(status) => status === true || status === 1,
	);
};

const batteryBand = (min, max) => (device) => {
	const percentage = getDeviceBattery(device);
	return Number.isFinite(percentage) && percentage > min && percentage <= max;
};

//Dashboard tiles and charts link here with ?health=<key> to show a pre-filtered list
const healthFilters = {
	offline: { predicate: (device) => device._online === false, labelKey: "devices.healthFilter.offline" },
	online: { predicate: (device) => device._online === true, labelKey: "devices.healthFilter.online" },
	"battery-low": { predicate: batteryBand(-Infinity, 20), labelKey: "devices.healthFilter.batteryLow" },
	"battery-21-40": { predicate: batteryBand(20, 40), label: "21-40%", categoryKey: "dashboard.charts.battery" },
	"battery-41-60": { predicate: batteryBand(40, 60), label: "41-60%", categoryKey: "dashboard.charts.battery" },
	"battery-61-80": { predicate: batteryBand(60, 80), label: "61-80%", categoryKey: "dashboard.charts.battery" },
	"battery-81-100": {
		predicate: batteryBand(80, Infinity),
		label: "81-100%",
		categoryKey: "dashboard.charts.battery",
	},
	"valves-closed": { predicate: hasClosedValve, labelKey: "devices.healthFilter.valvesClosed" },
	//Built from RSSI_BANDS so a chart bar and the list it links to always agree
	...Object.fromEntries(
		RSSI_BANDS.map((band) => [
			`rssi-${band.key}`,
			{
				predicate: (device) => getRssiBand(getDeviceRssi(device))?.key === band.key,
				labelKey: `device.state.wifiRssiQuality.${band.key}`,
				categoryKey: "dashboard.charts.rssi",
			},
		]),
	),
	"sensor-flowstop": {
		predicate: (device) => device.inputDevice?.includes("FLOWSTOP"),
		labelKey: "device.feature.flowstop",
		categoryKey: "devices.table.sensorType",
	},
	"sensor-flexipad": {
		predicate: (device) => device.inputDevice?.includes("FLEXIPAD"),
		labelKey: "device.feature.flexipad",
		categoryKey: "devices.table.sensorType",
	},
	"sensor-ranger": {
		predicate: (device) => device.inputDevice?.includes("RANGER"),
		labelKey: "device.feature.ranger",
		categoryKey: "devices.table.sensorType",
	},
	"actuator-flowstop": {
		predicate: (device) => device.outputDevice?.includes("FLOWSTOP"),
		labelKey: "device.feature.flowstop",
		categoryKey: "devices.table.actuatorType",
	},
};

const DevicesTable = ({ devicesData }) => {
	//Hooks
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const dt = useRef(null);

	//Recoil
	const [devices, setDevices] = useRecoilState(devicesListAtom);
	const [selectedDevice, setSelectedDevice] = useRecoilState(selectedDeviceAtom);
	const activeAlarms = useRecoilValue(deviceSensorAlarmListAtom);
	const getOfflineState = useDeviceOfflineState();

	// Local State
	const [dtFilters, setDtFilters] = useState(null);
	const [flexiGroupOptions, setFlexiGroupOptions] = useState([]);
	const [flowGroupOptions, setFlowGroupOptions] = useState([]);

	//On Page Load
	useEffect(() => {
		if (devicesData) {
			setDevices(devicesData);
			// setGroupFilterOptions(devicesData);
			initFilters();
		}
	}, [devicesData]);

	// const setGroupFilterOptions = async (devices) => {
	// 	for await (const device of devices) {
	// 		setFlexiGroupOptions((prevState) => _.union(prevState, device.alarmGroupsFlexi));
	// 		setFlowGroupOptions((prevState) => _.union(prevState, device.alarmGroupsFlow));
	// 	}
	// };

	const handleNavDevice = (device) => {
		if (device?.id) navigate(`/device/${device.id}`);
	};

	//Datatable Functions
	const initFilters = () => {
		setDtFilters({
			name: {
				operator: FilterOperator.AND,
				constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
			},
			id: {
				operator: FilterOperator.AND,
				constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
			},
			"zoneBreadcrumb.label": {
				operator: FilterOperator.AND,
				constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
			},
			// alarmGroupsFlexi: {
			// 	value: null,
			// 	matchMode: FilterMatchMode.CUSTOM,
			// },
			// alarmGroupsFlow: {
			// 	value: null,
			// 	matchMode: FilterMatchMode.CUSTOM,
			// },
			inputDevice: { value: null, matchMode: FilterMatchMode.CUSTOM },
			outputDevice: { value: null, matchMode: FilterMatchMode.CUSTOM },
			_online: { value: null, matchMode: FilterMatchMode.EQUALS },
			_inAlarm: { value: null, matchMode: FilterMatchMode.EQUALS },
		});
	};

	const exportCSV = (selectionOnly) => {
		dt.current.exportCSV({ selectionOnly });
	};

	// const alarmGroupFlexiRowTempate = (rowData) => {
	// 	const groupCount = rowData.alarmGroupsFlexi.length;
	// 	return <span>{t("devices.table.groupCount", { count: groupCount })}</span>;
	// };

	// const alarmGroupFlowRowTempate = (rowData) => {
	// 	const groupCount = rowData.alarmGroupsFlow.length;
	// 	return <span>{t("devices.table.groupCount", { count: groupCount })}</span>;
	// };

	// const flexiFilterTemplate = (options) => {
	// 	return (
	// 		<MultiSelect
	// 			showClear
	// 			value={options.value}
	// 			options={flexiGroupOptions}
	// 			// itemTemplate={featureFilterItemTemplate}
	// 			// selectedItemTemplate={featureFilterSelectedItemTemplate}
	// 			onChange={(e) => options.filterCallback(e.value)}
	// 			placeholder={t("common.form.multiselectPlaceholder")}
	// 		/>
	// 	);
	// };

	// const flexiFilterFunction = (value, filter) => {
	// 	let include = true;
	// 	if (filter) {
	// 		const checkFilter = _.intersection(value, filter);
	// 		if (!checkFilter.length) include = false;
	// 	}
	// 	return include;
	// };

	// const flowFilterTemplate = (options) => {
	// 	return (
	// 		<MultiSelect
	// 			showClear
	// 			value={options.value}
	// 			options={flowGroupOptions}
	// 			// itemTemplate={featureFilterItemTemplate}
	// 			// selectedItemTemplate={featureFilterSelectedItemTemplate}
	// 			onChange={(e) => options.filterCallback(e.value)}
	// 			placeholder={t("common.form.multiselectPlaceholder")}
	// 		/>
	// 	);
	// };

	// const flowFilterFunction = (value, filter) => {
	// 	let include = true;
	// 	if (filter) {
	// 		const checkFilter = _.intersection(value, filter);
	// 		if (!checkFilter.length) include = false;
	// 	}
	// 	return include;
	// };

const inputRowTemplate = (rowData) => {
		let deviceArray = [];

		if (rowData.inputDevice)
			deviceArray = rowData.inputDevice.map((d) => {
				return <FeatureItem key={uuid()} value={d} type="input" />;
			});

		return <div className="flex gapCol-medium gapRow-small">{deviceArray}</div>;
	};

	const inputFilterItemTemplate = (option) => {
		return <FeatureItem value={option.value} type="input" />;
	};
	const inputFilterSelectedItemTemplate = (option) => {
		if (option !== undefined) return <FeatureItem value={option} type="input" />;
	};

	const inputFilterTemplate = (options) => {
		return (
			<MultiSelect
				showClear
				value={options.value}
				options={inputDeviceOptions}
				itemTemplate={inputFilterItemTemplate}
				selectedItemTemplate={inputFilterSelectedItemTemplate}
				onChange={(e) => options.filterCallback(e.value)}
				placeholder={t("common.form.multiselectPlaceholder")}
			/>
		);
	};

	const deviceFilterFunction = (value, filter) => {
		// console.log("Filter");
		let include = true;
		if (filter?.length) {
			if (filter.includes(null) && value === null) {
				include = true;
			} else {
				const checkFilter = _.intersection(value, filter);
				if (!checkFilter.length) include = false;
			}
		}
		return include;
	};

	const outputRowTemplate = (rowData) => {
		let deviceArray = [];

		if (rowData.outputDevice)
			deviceArray = rowData.outputDevice.map((d) => {
				return <FeatureItem key={uuid()} value={d} type="output" />;
			});

		return <div className="flex gapCol-medium gapRow-small">{deviceArray}</div>;
	};

	const outputFilterItemTemplate = (option) => {
		return <FeatureItem value={option.value} type="output" />;
	};
	const outputFilterSelectedItemTemplate = (option) => {
		if (option !== undefined) return <FeatureItem value={option} type="output" />;
	};

	const outputFilterTemplate = (options) => {
		return (
			<MultiSelect
				showClear
				value={options.value}
				options={outputDeviceOptions}
				itemTemplate={outputFilterItemTemplate}
				selectedItemTemplate={outputFilterSelectedItemTemplate}
				onChange={(e) => options.filterCallback(e.value)}
				placeholder={t("common.form.multiselectPlaceholder")}
			/>
		);
	};

	//Live alarm state per device, driven by the alarm atom (AppSync subscription)
	const alarmedDeviceIds = new Set((activeAlarms ?? []).map((alarm) => alarm?.device?.id).filter(Boolean));

	//Health filter from dashboard click-through (?health=<key>)
	const healthKey = searchParams.get("health");
	const healthFilter = healthFilters[healthKey];
	//Connectivity and alarm state are independent dimensions - precompute both as filterable
	//fields. Connectivity is computed before the health filter runs, so the offline and online
	//health filters read the same field the column does rather than deciding offline a second time
	//and risking the two disagreeing.
	const devicesWithState = devices?.map((device) => ({
		...device,
		_online: !getOfflineState(device).isOffline,
		_inAlarm: alarmedDeviceIds.has(device.id),
	}));
	const visibleDevices =
		healthFilter && devicesWithState ? devicesWithState.filter(healthFilter.predicate) : devicesWithState;
	let healthFilterLabel = null;
	if (healthFilter) {
		const valueLabel = healthFilter.labelKey ? t(healthFilter.labelKey) : healthFilter.label;
		healthFilterLabel = healthFilter.categoryKey ? `${t(healthFilter.categoryKey)} - ${valueLabel}` : valueLabel;
	}

	const onlineFilterTemplate = (options) => (
		<Dropdown
			showClear
			value={options.value}
			options={[
				{ value: true, label: t("device.state.presence.online") },
				{ value: false, label: t("device.state.presence.offline") },
			]}
			onChange={(e) => options.filterCallback(e.value)}
			placeholder={t("common.form.multiselectPlaceholder")}
		/>
	);

	const connectivityRowTemplate = (rowData) => <ConnectivityPill device={rowData} showWarning />;

	const alarmRowTemplate = (rowData) =>
		rowData._inAlarm ? (
			<PillItem type="text" value={t("alarm.group.details.state.alarm")} wrapperStyle="error" />
		) : (
			<PillItem type="text" value={t("alarm.group.details.state.clear")} wrapperStyle="success" />
		);

	const alarmFilterTemplate = (options) => (
		<Dropdown
			showClear
			value={options.value}
			options={[
				{ value: true, label: t("alarm.group.details.state.alarm") },
				{ value: false, label: t("alarm.group.details.state.clear") },
			]}
			onChange={(e) => options.filterCallback(e.value)}
			placeholder={t("common.form.multiselectPlaceholder")}
		/>
	);

	const locationRowTemplate = (rowData) => {
		const unique = uuid();
		const locationBreadcrumb = rowData.zoneBreadcrumb?.label?.replaceAll("|", " > ");

		return (
			<>
				<Tooltip target={`.customTooltip-${unique}`} />
				<span
					className={`customTooltip-${unique}`}
					data-pr-tooltip={locationBreadcrumb}
					data-pr-at="right+10 center">
					{rowData.zone?.label}
				</span>
			</>
		);
	};

	return (
		<>
			<div className="flex jContent-spaceBetween aItems-center gap-small">
				<div>
					{healthFilter && (
						<div className="flex aItems-center gap-xsmall">
							<PillItem
								type="full"
								icon="pi-filter"
								value={`${t("devices.healthFilter.prefix")}: ${healthFilterLabel} (${
									visibleDevices?.length ?? 0
								})`}
								wrapperStyle="blue1"
							/>
							<Button
								icon="pi pi-times"
								className="noStyle fontColour-light"
								onClick={() => setSearchParams({})}
								tooltip={t("devices.healthFilter.clear")}
								aria-label={t("devices.healthFilter.clear")}
							/>
						</div>
					)}
				</div>
				<Button onClick={() => exportCSV(false)}>{t("common.table.export")}</Button>
			</div>
			<div className="marginTop-large">
				<DataTable
					ref={dt}
					value={visibleDevices}
					emptyMessage={t("common.table.noData")}
					selectionMode="single"
					selection={selectedDevice}
					onSelectionChange={(e) => {
						setSelectedDevice(e.value);
						handleNavDevice(e.value);
					}}
					sortMode="multiple"
					removableSort
					filters={dtFilters}
					filterDisplay="menu"
					autoLayout={true}
					paginator
					paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
					currentPageReportTemplate={t("common.table.paginatorTemplate")}
					rows={10}
					dataKey="id"
					exportFilename={t("common.table.exportFilename", { filename: "Devices", date: exportDate })}>
					<Column
						field="name"
						header={t("devices.table.name")}
						sortable
						filter
						filterApply={filterApplyTemplate}
					/>
					<Column
						field="id"
						header={t("devices.table.id")}
						dataType="text"
						sortable
						filter
						filterApply={filterApplyTemplate}
					/>
					<Column
						field="zone.label"
						header={t("devices.table.zone")}
						body={locationRowTemplate}
						sortable
						filter
						filterField="zoneBreadcrumb.label"
						filterApply={filterApplyTemplate}
					/>
					{/* <Column
					field="alarmGroupsInput"
					header={t("devices.table.alarmGroupFlexi")}
					body={alarmGroupFlexiRowTempate}
					sortable
					sortField="alarmGroupsFlexiCount"
					filter
					showFilterMatchModes={false}
					filterElement={flexiFilterTemplate}
					filterApply={filterApplyTemplate}
					filterFunction={flexiFilterFunction}
				/>
				<Column
					field="alarmGroupsOutput"
					header={t("devices.table.alarmGroupFlow")}
					body={alarmGroupFlowRowTempate}
					sortable
					sortField="alarmGroupsFlowCount"
					filter
					showFilterMatchModes={false}
					filterElement={flowFilterTemplate}
					filterApply={filterApplyTemplate}
					filterFunction={flowFilterFunction}
				/> */}
					<Column
						field="inputDevice"
						header={t(useSensorActuatorLabels ? "devices.table.sensorType" : "devices.table.input")}
						body={inputRowTemplate}
						sortable
						filter
						showFilterMatchModes={false}
						filterElement={inputFilterTemplate}
						filterFunction={deviceFilterFunction}
						filterApply={filterApplyTemplate}
					/>
					<Column
						field="outputDevice"
						header={t(useSensorActuatorLabels ? "devices.table.actuatorType" : "devices.table.output")}
						body={outputRowTemplate}
						sortable
						filter
						showFilterMatchModes={false}
						filterElement={outputFilterTemplate}
						filterFunction={deviceFilterFunction}
						filterApply={filterApplyTemplate}
					/>
					<Column
						field="_online"
						header={t("devices.table.connectivity")}
						body={connectivityRowTemplate}
						sortable
						filter
						showFilterMatchModes={false}
						filterElement={onlineFilterTemplate}
						filterApply={filterApplyTemplate}
					/>
					<Column
						field="_inAlarm"
						header={t("devices.table.alarmState")}
						body={alarmRowTemplate}
						sortable
						filter
						showFilterMatchModes={false}
						filterElement={alarmFilterTemplate}
						filterApply={filterApplyTemplate}
					/>
				</DataTable>
				{/* <pre>{JSON.stringify(devicesData, null, 2)}</pre> */}
			</div>
		</>
	);
};

export default DevicesTable;
